"use client";

import { type JSX } from "react";
import { useOnboarding } from "@/hooks/onboarding";
import { cn } from "@/lib/utils";
import { ONBOARDING_STEPS, ONBOARDING_STEP_ORDER, OnboardingStepType } from "@/store/onboardingStore";

interface OnboardingProgressBarProps {
  className?: string;
}

const stepLabels: Record<OnboardingStepType, { active: JSX.Element; completed: JSX.Element }> = {
  [ONBOARDING_STEPS.CREATE_ACCOUNT]: {
    active: (
      <>
        Create
        <br />
        Account
      </>
    ),
    completed: (
      <>
        Account
        <br />
        Created
      </>
    ),
  },
  [ONBOARDING_STEPS.LINK_CEX]: {
    active: (
      <>
        Connect
        <br />
        CEXs
      </>
    ),
    completed: (
      <>
        Connected
        <br />
        CEXs
      </>
    ),
  },
  [ONBOARDING_STEPS.CONNECT_SOCIAL]: {
    active: (
      <>
        Connect
        <br />
        Social
      </>
    ),
    completed: (
      <>
        Connected
        <br />
        Social
      </>
    ),
  },
};

export const OnboardingProgressBar = ({ className }: OnboardingProgressBarProps) => {
  const { currentStep } = useOnboarding();

  const currentStepIndex = ONBOARDING_STEP_ORDER.indexOf(currentStep);

  return (
    <div className={cn("w-full max-w-[488px] mx-auto flex flex-col gap-[19px]", className)}>
      {/* Progress bars */}
      <div className="flex gap-2">
        {ONBOARDING_STEP_ORDER.map((step, index) => {
          const isActive = index === currentStepIndex;

          return (
            <div
              key={step}
              className={cn("h-[3px] flex-1 rounded-full transition-all duration-500", {
                "bg-cede-primary-progress": isActive,
                "bg-cede-text-inactive": !isActive,
              })}
            />
          );
        })}
      </div>

      {/* Step labels */}
      <div className="flex gap-2">
        {ONBOARDING_STEP_ORDER.map((step, index) => {
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;

          return (
            <div
              key={step}
              className={cn("flex-1 text-[14px] font-medium leading-[140%] transition-colors duration-300", {
                "text-cede-text-primary": isActive,
                "text-cede-text-inactive": !isActive,
              })}
            >
              {isCompleted ? stepLabels[step].completed : stepLabels[step].active}
            </div>
          );
        })}
      </div>
    </div>
  );
};
